import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider';
import { useToast } from './hooks/ToastContext';
import { useNavigate } from 'react-router-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import {
  Checkbox1,
  ModernCard,
  WSButton
} from './Templates';
import Spinner from './common/Spinner';
import './SimpleAccountDelete.css';

/**
 * SimpleAccountDelete Component
 * For direct integration into Settings page
 * Immediately soft deletes account when confirmed
 */
function SimpleAccountDelete() {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');

  /**
   * Handle account deletion
   */
  const handleDeleteAccount = async () => {
    if (!acknowledged) {
      addToast({
        type: 'error',
        title: t('settings.delete_confirm_required_title'),
        description: t('settings.delete_confirm_required_desc')
      });
      return;
    }

    setIsLoading(true);
    try {
      // Call the simple deletion function
      const { data, error } = await supabase.rpc('delete_my_account', {
        deletion_reason_param: deletionReason || 'User deleted account from settings'
      });

      if (error) throw error;

      if (data.success) {
        addToast({
          type: 'success',
          title: t('settings.delete_success_title'),
          description: t('settings.delete_success_desc')
        });

        // Sign out the user
        await supabase.auth.signOut();
        
        // Redirect to home page
        navigate('/');
      } else {
        throw new Error(data.message || 'Failed to delete account');
      }

    } catch (error) {
      console.error('Error deleting account:', error);
      addToast({
        type: 'error',
        title: t('settings.delete_failed_title'),
        description: error.message || t('settings.delete_failed_desc')
      });
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  if (isLoading) {
    return (
      <div className="simple-delete-loading">
        <Spinner size="small" />
        <span>{t('settings.deleting')}</span>
      </div>
    );
  }

  return (
    <div className="simple-account-delete">
      {!showConfirmation ? (
        <div className="delete-button-container">
          <WSButton
            variant="danger"
            label={t('settings.delete_account')}
            onClick={() => setShowConfirmation(true)}
            icon={<FaExclamationTriangle />}
          />
          <p className="delete-info">
            {t('settings.delete_account_soft_description')}
          </p>
        </div>
      ) : (
        <ModernCard className="confirmation-card">
          <h3 className="confirmation-title">🔄 {t('settings.delete_account')}</h3>
          
          <div className="info-section">
            <p><strong>{t('settings.delete_flow_what_happens')}</strong></p>
            <ul>
              <li>✅ {t('settings.delete_flow_point_hidden')}</li>
              <li>✅ {t('settings.delete_flow_point_data_safe')}</li>
              <li>✅ {t('settings.delete_flow_point_restore')}</li>
              <li>❌ {t('settings.delete_flow_point_no_posts')}</li>
            </ul>
          </div>

          <div className="reason-section">
            <label htmlFor="deletion-reason">
              {t('settings.delete_flow_reason_label')}
            </label>
            <textarea
              id="deletion-reason"
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              placeholder={t('settings.delete_flow_reason_placeholder')}
              rows={2}
              maxLength={200}
            />
          </div>

          <div className="acknowledgment-section">
            <Checkbox1
              id="delete-acknowledgment"
              label={t('settings.delete_flow_acknowledge')}
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
          </div>

          <div className="confirmation-actions">
            <WSButton
              variant="secondary"
              label={t('buttons.cancel')}
              onClick={() => setShowConfirmation(false)}
            />
            <WSButton
              variant="danger"
              label={t('settings.delete_account')}
              onClick={handleDeleteAccount}
              disabled={!acknowledged || isLoading}
            />
          </div>
        </ModernCard>
      )}
    </div>
  );
}

export default SimpleAccountDelete;